import { useState, useEffect, useRef } from 'react';
import { api } from '../api';
import API_URL from '../api';

const TIPO_CONFIG = {
  cheque:           { label: '🔵 Cheque',        color: '#4cc9f0', bg: '#1a2a3e' },
  cheque_ventanilla:{ label: '🔵 Ch. Ventanilla', color: '#4cc9f0', bg: '#1a2a3e' },
  deposito:         { label: '🟢 Depósito',       color: '#4caf50', bg: '#1a3a2a' },
  transferencia:    { label: '🔀 Transferencia',  color: '#7209b7', bg: '#2a1a3a' },
  interno:          { label: '↔️ Interno',         color: '#aaa',    bg: '#2a2a2a' },
  iess:             { label: '🏛️ IESS',            color: '#ff9800', bg: '#3a2a1a' },
  comision:         { label: '💸 Comisión',        color: '#e63946', bg: '#3a1a1a' },
  iva_comision:     { label: '💸 IVA Com.',        color: '#e63946', bg: '#3a1a1a' },
  credito_bancario: { label: '🏦 Crédito banco',  color: '#f72585', bg: '#3a1a2a' },
  recaudacion:      { label: '💳 Recaudación',    color: '#4cc9f0', bg: '#1a2a3e' },
  seguro:           { label: '🛡️ Seguro',          color: '#ffc107', bg: '#3a3a1a' },
  impuesto:         { label: '📋 Impuesto',        color: '#ffc107', bg: '#3a3a1a' },
  ajuste:           { label: '⚖️ Ajuste',           color: '#aaa',    bg: '#2a2a2a' },
  otro:             { label: '❓ Otro',             color: '#555',    bg: '#1a1a1a' },
  pago_proveedor:   { label: '🏭 Pago Proveedor', color: '#f72585', bg: '#3a1a2a' },
  nomina:           { label: '👥 Nómina',          color: '#ff9800', bg: '#3a2a1a' },
  tarjeta:          { label: '💳 Tarjeta',         color: '#4cc9f0', bg: '#1a2a3e' },
  cobro_cliente:    { label: '💰 Cobro Cliente',   color: '#4caf50', bg: '#1a3a2a' },
};

const MESES_ES = ['','Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

function periodoLabel(p) {
  if (!p) return '';
  const [a, m] = p.split('-');
  return `${MESES_ES[parseInt(m)]} ${a}`;
}

export default function Banco() {
  const [vista, setVista]             = useState('lista');
  const [estados, setEstados]         = useState([]);
  const [detalle, setDetalle]         = useState(null);
  const [cargando, setCargando]       = useState(true);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [subiendo, setSubiendo]       = useState(false);
  const [errorMsg, setErrorMsg]       = useState('');
  const [exito, setExito]             = useState('');
  const [dragging, setDragging]       = useState(false);
  const [filtroTipo, setFiltroTipo]   = useState('todos');
  const [busqueda, setBusqueda]       = useState('');
  const [reconciliando, setReconciliando] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { cargarEstados(); }, []);

  const cargarEstados = async () => {
    setCargando(true);
    try {
      const data = await api.get('/banco/estados');
      setEstados(data.estados || []);
    } catch (e) { console.error(e); }
    finally { setCargando(false); }
  };

  const verDetalle = async (id, tipo = '') => {
    setCargandoDetalle(true);
    setFiltroTipo('todos');
    setBusqueda('');
    try {
      const data = await api.get(`/banco/estados/${id}${tipo ? `?tipo=${tipo}` : ''}`);
      if (!data.error) { setDetalle(data); setVista('detalle'); }
    } catch (e) { console.error(e); }
    finally { setCargandoDetalle(false); }
  };

  const subirPDF = async (archivos) => {
    if (!archivos || archivos.length === 0) return;
    const archivo = archivos[0];
    if (!archivo.name.toLowerCase().endsWith('.pdf')) {
      setErrorMsg('Solo se aceptan archivos PDF'); return;
    }
    setSubiendo(true); setErrorMsg(''); setExito('');
    try {
      const formData = new FormData();
      formData.append('archivo', archivo);
      const res  = await fetch(`${API_URL}/banco/upload`, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.error) {
        setErrorMsg(data.error);
      } else {
        setExito(`✅ ${periodoLabel(data.periodo)} — ${data.movimientos} movimientos · ${data.conciliados} cheques conciliados`);
        await cargarEstados();
      }
    } catch (e) { setErrorMsg('Error de conexión con el servidor'); }
    finally { setSubiendo(false); if (inputRef.current) inputRef.current.value = ''; }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este estado de cuenta?')) return;
    await api.delete(`/banco/estados/${id}`);
    cargarEstados();
  };

  const reconciliar = async () => {
    if (!detalle) return;
    setReconciliando(true);
    try {
      const data = await api.post(`/banco/estados/${detalle.estado.id}/conciliar`, {});
      if (!data.error) {
        setExito(`🔄 Conciliación completada: ${data.conciliados} cheques conciliados`);
        verDetalle(detalle.estado.id);
      }
    } catch (e) { console.error(e); }
    finally { setReconciliando(false); }
  };

  const onDragOver  = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = ()  => setDragging(false);
  const onDrop      = (e) => { e.preventDefault(); setDragging(false); subirPDF(e.dataTransfer.files); };

  // ── VISTA DETALLE ──
  if (vista === 'detalle' && detalle) {
    const { estado, movimientos, por_tipo } = detalle;

    const movsFiltrados = movimientos.filter(m => {
      const matchTipo = filtroTipo === 'todos' || m.tipo === filtroTipo;
      const matchBusq = !busqueda ||
        m.descripcion?.toLowerCase().includes(busqueda.toLowerCase()) ||
        m.referencia?.includes(busqueda);
      return matchTipo && matchBusq;
    });

    const chequesSinConciliar = movimientos.filter(m =>
      (m.tipo === 'cheque' || m.tipo === 'cheque_ventanilla') && !m.conciliado
    ).length;
    const chequesTotales = movimientos.filter(m =>
      m.tipo === 'cheque' || m.tipo === 'cheque_ventanilla'
    ).length;

    return (
      <div>
        {/* Header */}
        <div style={s.headerRow}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button style={s.botonVolver} onClick={() => { setVista('lista'); setDetalle(null); }}>
              ← Volver
            </button>
            <div>
              <h2 style={s.titulo}>🏦 {periodoLabel(estado.periodo)} — {estado.banco}</h2>
              <p style={s.subtitulo}>{estado.archivo} · {estado.total_movimientos} movimientos</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...s.botonAccion, opacity: reconciliando ? 0.6 : 1 }}
              onClick={reconciliar} disabled={reconciliando}>
              {reconciliando ? '🔄 Conciliando...' : '🔄 Re-conciliar'}
            </button>
            <button style={s.botonEliminar} onClick={() => eliminar(estado.id)}>🗑️</button>
          </div>
        </div>

        {exito && <div style={s.exitoBox}>{exito}</div>}

        {/* KPIs */}
        <div style={s.kpisRow}>
          {[
            ['Saldo Anterior',  `$${estado.saldo_anterior.toLocaleString('es-EC',{minimumFractionDigits:2})}`, '#aaa',    '#7209b7'],
            ['Saldo Final',     `$${estado.saldo_final.toLocaleString('es-EC',{minimumFractionDigits:2})}`,    '#fff',    '#4cc9f0'],
            ['Total Créditos',  `$${estado.total_creditos.toLocaleString('es-EC',{minimumFractionDigits:2})}`, '#4caf50', '#4caf50'],
            ['Total Débitos',   `$${estado.total_debitos.toLocaleString('es-EC',{minimumFractionDigits:2})}`,  '#e63946', '#e63946'],
            ['Cheques Conciliados', `${chequesTotales - chequesSinConciliar}/${chequesTotales}`,
              chequesSinConciliar === 0 ? '#4caf50' : '#ffc107',
              chequesSinConciliar === 0 ? '#4caf50' : '#ffc107'],
          ].map(([label, val, color, border]) => (
            <div key={label} style={{ ...s.kpiCard, borderTopColor: border }}>
              <div style={{ ...s.kpiValor, color, fontSize: 18 }}>{val}</div>
              <div style={s.kpiLabel}>{label}</div>
            </div>
          ))}
        </div>

        {/* Resumen por tipo */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <button
            style={{ ...s.filtroBtn, ...(filtroTipo === 'todos' ? s.filtroBtnActivo : {}) }}
            onClick={() => setFiltroTipo('todos')}>
            Todos ({movimientos.length})
          </button>
          {por_tipo.map(pt => {
            const cfg = TIPO_CONFIG[pt.tipo] || TIPO_CONFIG.otro;
            return (
              <button key={pt.tipo}
                style={{ ...s.filtroBtn, ...(filtroTipo === pt.tipo ? { ...s.filtroBtnActivo, borderColor: cfg.color, color: cfg.color } : {}) }}
                onClick={() => setFiltroTipo(pt.tipo)}>
                {cfg.label.split(' ').slice(0,2).join(' ')} ({pt.cantidad})
                {pt.conciliados > 0 && <span style={{ color: '#4caf50', marginLeft: 4 }}>✅{pt.conciliados}</span>}
              </button>
            );
          })}
        </div>

        {/* Buscador */}
        <input
          style={{ ...s.inputBusqueda, marginBottom: 12 }}
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          placeholder="Buscar por descripción o número de referencia..."
        />

        {/* Tabla movimientos */}
        <div style={s.tabla}>
          <div style={s.tablaHeader}>
            <span style={{ width: 95 }}>FECHA</span>
            <span style={{ flex: 4 }}>DESCRIPCIÓN</span>
            <span style={{ width: 140, textAlign: 'center' }}>TIPO</span>
            <span style={{ width: 110, textAlign: 'right' }}>DÉBITO</span>
            <span style={{ width: 110, textAlign: 'right' }}>CRÉDITO</span>
            <span style={{ width: 120, textAlign: 'right' }}>SALDO</span>
            <span style={{ width: 160, textAlign: 'center' }}>ESTADO</span>
          </div>
          <div style={{ maxHeight: 'calc(100vh - 420px)', overflowY: 'auto' }}>
            {movsFiltrados.map((m, i) => {
              const cfg = TIPO_CONFIG[m.tipo] || TIPO_CONFIG.otro;
              return (
                <div key={m.id} style={{
                  ...s.tablaRow,
                  background: i%2===0 ? '#1e1e2e' : '#1a1a2e',
                  borderLeft: `3px solid ${m.conciliado ? '#4caf50' : m.debito ? '#2d2d44' : '#4caf5033'}`,
                }}>
                  <span style={{ width: 95, color: '#aaa', fontSize: 11 }}>{m.fecha}</span>
                  <span style={{ flex: 4, color: '#fff', fontSize: 12 }}>
                    {m.descripcion}
                    {m.referencia && <span style={{ color: '#555', marginLeft: 8, fontSize: 11 }}>#{m.referencia}</span>}
                  </span>
                  <span style={{ width: 140, textAlign: 'center' }}>
                    <span style={{ background: cfg.bg, color: cfg.color, borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 'bold' }}>
                      {cfg.label}
                    </span>
                  </span>
                  <span style={{ width: 110, textAlign: 'right', color: '#e63946', fontWeight: 'bold', fontSize: 12 }}>
                    {m.debito ? `$${m.debito.toLocaleString('es-EC',{minimumFractionDigits:2})}` : ''}
                  </span>
                  <span style={{ width: 110, textAlign: 'right', color: '#4caf50', fontWeight: 'bold', fontSize: 12 }}>
                    {m.credito ? `$${m.credito.toLocaleString('es-EC',{minimumFractionDigits:2})}` : ''}
                  </span>
                  <span style={{ width: 120, textAlign: 'right', color: '#aaa', fontSize: 11 }}>
                    {m.saldo ? `$${m.saldo.toLocaleString('es-EC',{minimumFractionDigits:2})}` : ''}
                  </span>
                  <span style={{ width: 160, textAlign: 'center' }}>
                    {m.conciliado ? (
                      <span style={{ color: '#4caf50', fontSize: 10, fontWeight: 'bold' }}>
                        ✅ {m.conciliado_con?.split(' ').slice(0,2).join(' ')}
                      </span>
                    ) : (
                      <span style={{ color: '#333', fontSize: 10 }}>—</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
          {/* Footer totales filtrados */}
          {filtroTipo !== 'todos' && (
            <div style={{ display: 'flex', padding: '10px 16px', background: '#0f0f1a', gap: 8, borderTop: '2px solid #2d2d44' }}>
              <span style={{ flex: 5, color: '#aaa', fontSize: 12 }}>{movsFiltrados.length} movimientos filtrados</span>
              <span style={{ width: 110, textAlign: 'right', color: '#e63946', fontWeight: 'bold' }}>
                ${movsFiltrados.reduce((a,m) => a + (m.debito||0), 0).toLocaleString('es-EC',{minimumFractionDigits:2})}
              </span>
              <span style={{ width: 110, textAlign: 'right', color: '#4caf50', fontWeight: 'bold' }}>
                ${movsFiltrados.reduce((a,m) => a + (m.credito||0), 0).toLocaleString('es-EC',{minimumFractionDigits:2})}
              </span>
              <span style={{ width: 280 }} />
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── VISTA LISTA ──
  return (
    <div>
      <div style={s.headerRow}>
        <div>
          <h2 style={s.titulo}>🏦 Banco</h2>
          <p style={s.subtitulo}>Estados de cuenta · conciliación automática con Morpheus</p>
        </div>
        <button style={s.botonRefresh} onClick={cargarEstados}>🔄 Actualizar</button>
      </div>

      {/* Drop zone */}
      <div style={{ ...s.dropZone, ...(dragging ? s.dropZoneActivo : {}) }}
        onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
        {subiendo ? (
          <div style={{ textAlign: 'center' }}>
            <div style={s.spinner} />
            <p style={{ color: '#4cc9f0', marginTop: 12, fontSize: 14 }}>Procesando estado de cuenta...</p>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 36, marginBottom: 8 }}>🏦</div>
            <p style={{ color: '#fff', fontWeight: 'bold', marginBottom: 4 }}>
              Arrastra el PDF del estado de cuenta o haz click para seleccionar
            </p>
            <p style={{ color: '#555', fontSize: 12, marginBottom: 12 }}>
              Banco Internacional · extracción automática · conciliación con Morpheus
            </p>
            <input ref={inputRef} type="file" accept=".pdf" style={{ display: 'none' }}
              onChange={e => e.target.files.length > 0 && subirPDF(e.target.files)} />
            <button style={s.botonSeleccionar} onClick={() => inputRef.current?.click()}>
              Seleccionar PDF
            </button>
          </>
        )}
      </div>

      {errorMsg && <div style={s.errorBox}>{errorMsg}</div>}
      {exito    && <div style={s.exitoBox}>{exito}</div>}

      {/* Lista estados */}
      {cargando ? (
        <p style={s.cargando}>Cargando estados de cuenta...</p>
      ) : estados.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#555' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🏦</div>
          <p>No hay estados de cuenta cargados.</p>
          <p style={{ fontSize: 12, marginTop: 8 }}>Sube el PDF del Banco Internacional para empezar.</p>
        </div>
      ) : (
        <div style={s.tabla}>
          <div style={s.tablaHeader}>
            <span style={{ width: 100 }}>PERÍODO</span>
            <span style={{ width: 130, textAlign: 'right' }}>SALDO ANT.</span>
            <span style={{ width: 130, textAlign: 'right' }}>SALDO FINAL</span>
            <span style={{ width: 130, textAlign: 'right' }}>CRÉDITOS</span>
            <span style={{ width: 130, textAlign: 'right' }}>DÉBITOS</span>
            <span style={{ width: 70,  textAlign: 'center' }}>MOVS.</span>
            <span style={{ flex: 2,    textAlign: 'center' }}>CHEQUES CONCILIADOS</span>
            <span style={{ width: 80,  textAlign: 'center' }}>VER</span>
            <span style={{ width: 50,  textAlign: 'center' }}>DEL</span>
          </div>
          {estados.map((e, i) => {
            const pct = e.cheques_total > 0
              ? Math.round(e.cheques_conciliados / e.cheques_total * 100)
              : 100;
            const colorConc = pct === 100 ? '#4caf50' : pct >= 80 ? '#ffc107' : '#e63946';
            return (
              <div key={e.id} style={{
                ...s.tablaRow,
                background: i%2===0 ? '#1e1e2e' : '#1a1a2e',
                cursor: 'pointer',
              }}
                onClick={() => verDetalle(e.id)}
                onMouseEnter={ev => ev.currentTarget.style.background = '#2d2d44'}
                onMouseLeave={ev => ev.currentTarget.style.background = i%2===0?'#1e1e2e':'#1a1a2e'}
              >
                <span style={{ width: 100 }}>
                  <div style={{ color: '#fff', fontWeight: 'bold', fontSize: 14 }}>{periodoLabel(e.periodo)}</div>
                  <div style={{ color: '#555', fontSize: 10 }}>{e.banco}</div>
                </span>
                <span style={{ width: 130, textAlign: 'right', color: '#aaa', fontSize: 13 }}>
                  ${e.saldo_anterior.toLocaleString('es-EC',{minimumFractionDigits:2})}
                </span>
                <span style={{ width: 130, textAlign: 'right', color: '#4cc9f0', fontWeight: 'bold', fontSize: 13 }}>
                  ${e.saldo_final.toLocaleString('es-EC',{minimumFractionDigits:2})}
                </span>
                <span style={{ width: 130, textAlign: 'right', color: '#4caf50', fontSize: 13 }}>
                  ${e.total_creditos.toLocaleString('es-EC',{minimumFractionDigits:2})}
                </span>
                <span style={{ width: 130, textAlign: 'right', color: '#e63946', fontSize: 13 }}>
                  ${e.total_debitos.toLocaleString('es-EC',{minimumFractionDigits:2})}
                </span>
                <span style={{ width: 70, textAlign: 'center', color: '#aaa' }}>{e.total_movimientos}</span>
                <span style={{ flex: 2, textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <div style={{ flex: 1, maxWidth: 120, height: 6, background: '#2d2d44', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: colorConc, borderRadius: 3, transition: 'width 0.5s' }} />
                    </div>
                    <span style={{ color: colorConc, fontWeight: 'bold', fontSize: 12, minWidth: 60 }}>
                      {e.cheques_conciliados}/{e.cheques_total} ({pct}%)
                    </span>
                  </div>
                </span>
                <span style={{ width: 80, textAlign: 'center' }}>
                  <button style={s.botonVer} onClick={ev => { ev.stopPropagation(); verDetalle(e.id); }}>
                    Ver →
                  </button>
                </span>
                <span style={{ width: 50, textAlign: 'center' }}>
                  <button style={s.botonDel} onClick={ev => { ev.stopPropagation(); eliminar(e.id); }}>
                    🗑️
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const s = {
  headerRow:       { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  titulo:          { color: '#fff', marginBottom: 4 },
  subtitulo:       { color: '#555', fontSize: 13 },
  botonRefresh:    { background: 'none', border: '1px solid #444', borderRadius: 8, color: '#aaa', padding: '8px 16px', cursor: 'pointer', fontSize: 13 },
  botonVolver:     { background: 'none', border: '1px solid #444', borderRadius: 8, color: '#aaa', padding: '8px 16px', cursor: 'pointer', fontSize: 13 },
  botonAccion:     { background: '#1a2a3e', border: '1px solid #4cc9f0', borderRadius: 8, color: '#4cc9f0', padding: '8px 16px', cursor: 'pointer', fontWeight: 'bold', fontSize: 13 },
  botonEliminar:   { background: 'none', border: '1px solid #3a1a1a', borderRadius: 8, color: '#e63946', padding: '8px 12px', cursor: 'pointer', fontSize: 13 },
  botonSeleccionar:{ background: '#1a2a3e', border: '1px solid #4cc9f0', borderRadius: 8, color: '#4cc9f0', padding: '10px 24px', cursor: 'pointer', fontWeight: 'bold', fontSize: 14 },
  botonVer:        { background: 'none', border: '1px solid #4cc9f0', borderRadius: 6, color: '#4cc9f0', padding: '4px 12px', cursor: 'pointer', fontSize: 12 },
  botonDel:        { background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 14, padding: '4px' },
  cargando:        { color: '#aaa', padding: 40, textAlign: 'center' },
  dropZone:        { border: '2px dashed #2d2d44', borderRadius: 12, padding: 36, textAlign: 'center', marginBottom: 20, transition: 'border-color 0.2s, background 0.2s' },
  dropZoneActivo:  { borderColor: '#4cc9f0', background: '#0f1a2a' },
  spinner:         { width: 36, height: 36, border: '3px solid #2d2d44', borderTopColor: '#4cc9f0', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' },
  errorBox:        { background: '#3a1a1a', border: '1px solid #e63946', borderRadius: 8, color: '#e63946', padding: '12px 16px', marginBottom: 16, fontSize: 13 },
  exitoBox:        { background: '#1a3a2a', border: '1px solid #4caf50', borderRadius: 8, color: '#4caf50', padding: '12px 16px', marginBottom: 16, fontSize: 13 },
  kpisRow:         { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' },
  kpiCard:         { background: '#1e1e2e', borderRadius: 10, padding: '16px 20px', flex: 1, minWidth: 130, borderTop: '3px solid #7209b7' },
  kpiValor:        { fontSize: 18, fontWeight: 'bold', color: '#fff', marginBottom: 4 },
  kpiLabel:        { color: '#aaa', fontSize: 11 },
  filtroBtn:       { background: '#1e1e2e', border: '1px solid #2d2d44', borderRadius: 6, color: '#888', padding: '5px 12px', cursor: 'pointer', fontSize: 11 },
  filtroBtnActivo: { background: '#2d2d44', border: '1px solid #4cc9f0', color: '#4cc9f0', fontWeight: 'bold' },
  inputBusqueda:   { width: '100%', background: '#0f0f1a', border: '1px solid #2d2d44', borderRadius: 8, color: '#fff', padding: '10px 14px', fontSize: 13, boxSizing: 'border-box' },
  tabla:           { background: '#1e1e2e', borderRadius: 12, overflow: 'hidden' },
  tablaHeader:     { display: 'flex', padding: '10px 16px', background: '#0f0f1a', color: '#555', fontSize: 11, fontWeight: 'bold', gap: 8 },
  tablaRow:        { display: 'flex', padding: '12px 16px', gap: 8, alignItems: 'center', borderBottom: '1px solid #0f0f1a', transition: 'background 0.15s' },
};